/**
 * Server Filter Panel - OData $filter Builder UI
 *
 * Enables visual creation of OData filter expressions.
 * Supports all OData operators and variables like today(), now(), etc.
 */

import React, { useState, useCallback } from 'react';
import {
  Plus,
  Trash2,
  Calendar,
  Clock,
  ChevronDown,
  Filter,
  Code,
  Copy,
  Check,
} from 'lucide-react';
import type { ODataFilter } from '../../services/odataService';
import { ODataQueryBuilder } from '../../services/odataService';

interface ServerFilterPanelProps {
  filters: ODataFilter[];
  availableFields: Array<{ name: string; dataType: string }>;
  onChange: (filters: ODataFilter[]) => void;
  entityName?: string;
}

// Filter Operators with Labels
const FILTER_OPERATORS = [
  { value: 'eq', label: '= Equal', category: 'compare' },
  { value: 'ne', label: '≠ Not equal', category: 'compare' },
  { value: 'gt', label: '> Greater than', category: 'compare' },
  { value: 'ge', label: '≥ Greater or equal', category: 'compare' },
  { value: 'lt', label: '< Less than', category: 'compare' },
  { value: 'le', label: '≤ Less or equal', category: 'compare' },
  { value: 'contains', label: 'Contains', category: 'string' },
  { value: 'startswith', label: 'Starts with', category: 'string' },
  { value: 'endswith', label: 'Ends with', category: 'string' },
] as const;

// Variables for dynamic values
const VARIABLE_PRESETS = [
  { category: 'Date', items: [
    { value: 'today()', label: 'Today', icon: Calendar },
    { value: 'yesterday()', label: 'Yesterday', icon: Calendar },
    { value: 'tomorrow()', label: 'Tomorrow', icon: Calendar },
  ]},
  { category: 'Time', items: [
    { value: 'now()', label: 'Now', icon: Clock },
    { value: 'startOfWeek()', label: 'Start of Week', icon: Calendar },
    { value: 'endOfWeek()', label: 'End of Week', icon: Calendar },
  ]},
  { category: 'Relative', items: [
    { value: 'daysAgo(7)', label: '7 Days Ago', icon: Calendar },
    { value: 'daysAgo(30)', label: '30 Days Ago', icon: Calendar },
    { value: 'daysFromNow(7)', label: 'In 7 Days', icon: Calendar },
    { value: 'daysFromNow(30)', label: 'In 30 Days', icon: Calendar },
  ]},
];

export function ServerFilterPanel({
  filters,
  availableFields,
  onChange,
  entityName,
}: ServerFilterPanelProps) {
  const [showVariables, setShowVariables] = useState<number | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [copied, setCopied] = useState(false);

  // Add new filter
  const addFilter = useCallback(() => {
    const newFilter: ODataFilter = {
      field: availableFields[0]?.name || '',
      operator: 'eq',
      value: '',
      logic: filters.length > 0 ? 'and' : undefined,
    };
    onChange([...filters, newFilter]);
  }, [filters, availableFields, onChange]);

  // Update filter
  const updateFilter = useCallback((index: number, updates: Partial<ODataFilter>) => {
    const newFilters = [...filters];
    newFilters[index] = { ...newFilters[index], ...updates };
    onChange(newFilters);
  }, [filters, onChange]);

  // Remove filter
  const removeFilter = useCallback((index: number) => {
    const newFilters = filters.filter((_, i) => i !== index);
    // Remove logic from first filter
    if (newFilters.length > 0 && newFilters[0].logic) {
      newFilters[0] = { ...newFilters[0], logic: undefined };
    }
    onChange(newFilters);
  }, [filters, onChange]);

  // Insert variable into value
  const insertVariable = useCallback((index: number, variable: string) => {
    updateFilter(index, { value: variable });
    setShowVariables(null);
  }, [updateFilter]);

  // Build preview query
  const previewQuery = React.useMemo(() => {
    if (filters.length === 0) return '';
    const builder = new ODataQueryBuilder(entityName || 'Entity');
    builder.filter(filters);
    return builder.buildParamsOnly();
  }, [filters, entityName]);

  // Copy to clipboard
  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(previewQuery);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [previewQuery]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-500" />
          <h3 className="font-medium text-gray-900">Server Filter (OData)</h3>
        </div>
        <button
          onClick={addFilter}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100"
        >
          <Plus className="w-4 h-4" />
          Add Filter
        </button>
      </div>

      {/* Info */}
      <p className="text-sm text-gray-500">
        Server filters are sent directly to the Infor LN API. This is more efficient than client-side filtering.
      </p>

      {/* Filter List */}
      {filters.length === 0 ? (
        <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500 text-sm">
          No filters defined. Click "Add Filter" to get started.
        </div>
      ) : (
        <div className="space-y-2">
          {filters.map((filter, index) => (
            <div
              key={index}
              className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg"
            >
              {/* Logic Operator (AND/OR) */}
              {index > 0 && (
                <select
                  value={filter.logic || 'and'}
                  onChange={(e) => updateFilter(index, { logic: e.target.value as 'and' | 'or' })}
                  className="px-2 py-1 text-xs font-medium bg-white border border-gray-300 rounded"
                >
                  <option value="and">AND</option>
                  <option value="or">OR</option>
                </select>
              )}

              {/* Field Selection */}
              <select
                value={filter.field}
                onChange={(e) => updateFilter(index, { field: e.target.value })}
                className="flex-1 min-w-[150px] px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">-- Select Field --</option>
                {availableFields.map((field) => (
                  <option key={field.name} value={field.name}>
                    {field.name} ({field.dataType})
                  </option>
                ))}
              </select>

              {/* Operator Selection */}
              <select
                value={filter.operator}
                onChange={(e) => updateFilter(index, { operator: e.target.value as ODataFilter['operator'] })}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <optgroup label="Comparison">
                  {FILTER_OPERATORS.filter(o => o.category === 'compare').map((op) => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </optgroup>
                <optgroup label="Text">
                  {FILTER_OPERATORS.filter(o => o.category === 'string').map((op) => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </optgroup>
              </select>

              {/* Value Input */}
              <div className="relative flex-1 min-w-[200px]">
                <input
                  type="text"
                  value={String(filter.value ?? '')}
                  onChange={(e) => updateFilter(index, { value: e.target.value })}
                  placeholder="Enter value or select variable..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                />
                <button
                  onClick={() => setShowVariables(showVariables === index ? null : index)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-blue-500"
                  title="Insert variable"
                >
                  <Calendar className="w-4 h-4" />
                </button>

                {/* Variable Dropdown */}
                {showVariables === index && (
                  <div className="absolute z-10 right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200">
                    <div className="p-2 border-b border-gray-100">
                      <span className="text-xs font-medium text-gray-500">Variables</span>
                    </div>
                    {VARIABLE_PRESETS.map((group) => (
                      <div key={group.category}>
                        <div className="px-3 py-1 text-xs text-gray-400 bg-gray-50">
                          {group.category}
                        </div>
                        {group.items.map((item) => (
                          <button
                            key={item.value}
                            onClick={() => insertVariable(index, item.value)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-blue-50"
                          >
                            <item.icon className="w-4 h-4 text-gray-400" />
                            <span>{item.label}</span>
                            <code className="ml-auto text-xs text-gray-400">{item.value}</code>
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Remove Button */}
              <button
                onClick={() => removeFilter(index)}
                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                title="Remove filter"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Query Preview */}
      {filters.length > 0 && (
        <div className="mt-4">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
          >
            <Code className="w-4 h-4" />
            <span>OData Query Preview</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showPreview ? 'rotate-180' : ''}`} />
          </button>

          {showPreview && (
            <div className="mt-2 p-3 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-400">$filter Parameter</span>
                <button
                  onClick={copyToClipboard}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-white"
                >
                  {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <code className="block text-sm text-green-400 font-mono break-all">
                {previewQuery || '(no filter)'}
              </code>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ServerFilterPanel;
